import { useDeleteProject } from '@/features/projects/hooks';
import { useMutateDeleteChat } from '@/hooks/mutation/useMutateDeleteChat';
import { cn } from '@/lib/cn';
import { getChatListVersion } from '@heidi/agents/contracts';
import { ContextMenuItem } from '@heidi/design-system/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { SidebarMenuAction } from '@heidi/design-system/page-shell';
import { toast } from '@heidi/design-system/toast';
import { Copy, Ellipsis, Pencil, Pin, Trash2 } from 'lucide-react';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { AGENT_HOME_ROW_ACTION } from '../agent-home-nav-context';
import { trackNavV3ButtonClicked } from '../nav-v3-analytics';
import { navV3Messages } from '../nav-v3.messages';
import { useNavSavedChats } from '../saved-chats-context';
import { ChatRowDeleteDialog } from './chat-row-delete-dialog';

export interface ChatRowActionsMenuChat {
  id: string;
  title: string;
  version?: 1 | 2;
}

export type ChatRowActionsMenuSlots = {
  action: ReactNode;
  contextItems: ReactNode;
};

type ChatRowMenuItem = typeof DropdownMenuItem | typeof ContextMenuItem;

export interface ChatRowActionsMenuProps {
  chat: ChatRowActionsMenuChat;
  /** True inside the Pinned section, where every row is already pinned by
   * construction — the fast-path default for the Pin/Unpin label. Rows in
   * Recents fall back to a live `isChatSaved` check, since a chat can be
   * both recent and pinned at once. */
  isPinnedSection: boolean;
  onRenameStart: () => void;
  /** Called after the version-correct delete mutation succeeds. */
  onDeleteSuccess: () => void;
  /**
   * When set, the kebab is `action` and the same items are `contextItems` so
   * the row can attach them to `NavItem.rowContextMenu` (right-click).
   */
  renderRow?: (slots: ChatRowActionsMenuSlots) => ReactNode;
}

/**
 * Hover/focus-revealed ellipsis action menu for a nav chat row — Pin/Unpin,
 * Rename, Copy chat ID, Delete. Lives inside the nav (not
 * `src/components/chat/**`), so it may import `@/hooks/mutation/*` and
 * `@/hooks/useSavedChats` directly (spec constraint C1) — but reads the
 * saved-chats state through `useNavSavedChats()` instead, the context
 * `SavedChatsProvider` mounts once for the whole nav-v3 tree, so N rendered
 * rows share one set of query/mutation subscriptions rather than mounting
 * `useSavedChats()` (two queries, two mutation observers) once each.
 *
 * The same items also render into a right-click menu so nav-v3 recents match
 * the pre-v3 Chat sidebar.
 */
export const ChatRowActionsMenu = ({
  chat,
  isPinnedSection,
  onRenameStart,
  onDeleteSuccess,
  renderRow,
}: ChatRowActionsMenuProps) => {
  const intl = useIntl();
  const { isChatSaved, toggleSaveChat } = useNavSavedChats();
  const deleteChat = useMutateDeleteChat();
  const deleteProject = useDeleteProject();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // Radix's default close behaviour returns focus to the trigger, which is
  // what keyboard users expect after Escape or selecting Pin/Copy chat
  // ID/Delete — suppressed only for Rename, where `ChatRowRenameInput` needs
  // to grab focus itself instead (see its own doc comment).
  const suppressCloseAutoFocusRef = useRef(false);

  const isPinned = isPinnedSection || isChatSaved(chat.id);
  const isV2 = getChatListVersion(chat) === 2;
  const isDeleting = isV2 ? deleteProject.isPending : deleteChat.isPending;

  const handleTogglePin = useCallback(() => {
    trackNavV3ButtonClicked({
      label: isPinned ? 'nav_v3_chat_row_unpin' : 'nav_v3_chat_row_pin',
      chat_id: chat.id,
    });
    toggleSaveChat(chat.id);
  }, [isPinned, toggleSaveChat, chat.id]);

  const handleRename = useCallback(() => {
    trackNavV3ButtonClicked({ label: 'nav_v3_chat_row_rename', chat_id: chat.id });
    suppressCloseAutoFocusRef.current = true;
    onRenameStart();
  }, [onRenameStart, chat.id]);

  const handleCopyChatId = useCallback(() => {
    trackNavV3ButtonClicked({ label: 'nav_v3_chat_row_copy_chat_id', chat_id: chat.id });
    void navigator.clipboard
      .writeText(chat.id)
      .then(() => {
        toast({
          title: intl.formatMessage(navV3Messages.chatRowChatIdCopied),
          tone: 'success',
        });
      })
      .catch(() => {
        toast({
          title: intl.formatMessage(navV3Messages.chatRowCopyFailed),
          tone: 'destructive',
        });
      });
  }, [chat.id, intl]);

  const handleDeleteClick = useCallback(() => {
    trackNavV3ButtonClicked({ label: 'nav_v3_chat_row_delete', chat_id: chat.id });
    setIsDeleteDialogOpen(true);
  }, [chat.id]);

  const handleConfirmDelete = useCallback(() => {
    if (isV2) {
      deleteProject.mutate(chat.id, {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          onDeleteSuccess();
        },
        onError: () => {
          toast({
            tone: 'destructive',
            title: intl.formatMessage(navV3Messages.chatRowDeleteFailed),
          });
        },
      });
      return;
    }
    deleteChat.mutate(chat.id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        onDeleteSuccess();
      },
      // v1 delete's own `onError` already surfaces a toast.
    });
  }, [isV2, deleteProject, deleteChat, chat.id, onDeleteSuccess, intl]);

  const pinLabel = isPinned
    ? intl.formatMessage(navV3Messages.chatRowUnpin)
    : intl.formatMessage(navV3Messages.chatRowPin);

  const renderItems = (Item: ChatRowMenuItem) => (
    <>
      <Item data-testid="chat-row-menu-pin" onSelect={handleTogglePin}>
        <Pin aria-hidden className={cn(isPinned && 'fill-current')} />
        {pinLabel}
      </Item>
      <Item data-testid="chat-row-menu-rename" onSelect={handleRename}>
        <Pencil aria-hidden />
        {intl.formatMessage(navV3Messages.chatRowRename)}
      </Item>
      <Item data-testid="chat-row-menu-copy-chat-id" onSelect={handleCopyChatId}>
        <Copy aria-hidden />
        {intl.formatMessage(navV3Messages.chatRowCopyChatId)}
      </Item>
      <Item
        data-testid="chat-row-menu-delete"
        variant="destructive"
        onSelect={handleDeleteClick}
      >
        <Trash2 aria-hidden />
        {intl.formatMessage(navV3Messages.chatRowDeleteDialogConfirm)}
      </Item>
    </>
  );

  const action = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuAction
          showOnHover
          aria-label={intl.formatMessage(navV3Messages.chatRowMoreActions)}
          data-testid="nav-chat-row-actions-trigger"
          className={AGENT_HOME_ROW_ACTION}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Ellipsis aria-hidden />
        </SidebarMenuAction>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="bottom"
        sideOffset={4}
        onClick={(e) => e.stopPropagation()}
        onCloseAutoFocus={(event) => {
          if (!suppressCloseAutoFocusRef.current) return;
          suppressCloseAutoFocusRef.current = false;
          // Keep focus from bouncing back to the trigger on close — that
          // would race the rename input's own focus grab (see
          // `ChatRowRenameInput`). Every other close (Escape, outside
          // click, Pin/Copy chat ID/Delete) keeps Radix's default so
          // keyboard focus lands back on the trigger.
          event.preventDefault();
        }}
      >
        {renderItems(DropdownMenuItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const contextItems = renderItems(ContextMenuItem);

  return (
    <>
      {renderRow ? renderRow({ action, contextItems }) : action}
      <ChatRowDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
